import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, forwardRef, output } from '@angular/core';
import { UntypedFormBuilder, FormGroup, UntypedFormControl, Validators, ValidatorFn, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MessageService } from 'base';
import { ConfigurationService } from '../../services/configuration.service';
import { OnDestroy } from '@angular/core';
import { ComponentBase } from 'common';
import { StringUtils } from 'DiscoveryEngine.TS/Utils';
import { Integration, IntegrationNames, IntegrationTable } from '../../../DiscoveryEngine.TS/Core/Integration';
import { DefaultIntegrationSettingsChild } from '../default-integration-settings-child-interface/default-integration-settings-child-interface';


@Component({
	selector: 'halo-default-settings',
	templateUrl: './halo-default-settings.component.html',
	styleUrls: ['./halo-default-settings.component.scss'],
})
export class HaloDefaultSettingsComponent extends ComponentBase implements DefaultIntegrationSettingsChild, OnInit, OnDestroy, OnChanges {
	@Input()
	public inputSettings: Integration;

	public form: UntypedFormGroup;

	public enabled: UntypedFormControl;
	public useMacAddressLookup: UntypedFormControl;
	public useIpAddressLookup: UntypedFormControl;

	public serverURL: UntypedFormControl;
	public apiClientId: UntypedFormControl;
	public apiUsername: UntypedFormControl;
	public apiPassword: UntypedFormControl;

	private apiUsernameChanged: boolean;
	private apiUsernameChangedSub: Subscription;

	private apiPasswordChanged: boolean;
	private apiPasswordChangedSub: Subscription;

	private apiClientIdChanged: boolean;
	private apiClientIdChangedSub: Subscription;

	constructor(private fb: UntypedFormBuilder,
		private configService: ConfigurationService,
		private messageService: MessageService) {

		super();
	}

	public ngOnInit(): void {
		this.buildForm();
	}

	public ngOnDestroy(): void {
		if (this.apiUsernameChangedSub) {
			this.apiUsernameChangedSub.unsubscribe();
		}
		if (this.apiPasswordChangedSub) {
			this.apiPasswordChangedSub.unsubscribe();
		}
		if (this.apiClientIdChangedSub) {
			this.apiClientIdChangedSub.unsubscribe();
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.buildForm();
	}

	private buildForm(): void {
		this.enabled = undefined;
		this.useMacAddressLookup = undefined;
		this.useIpAddressLookup = undefined;

		this.serverURL = undefined;
		this.apiClientId = undefined;
		this.apiUsername = undefined;
		this.apiPassword = undefined;

		let enabled = true;
		let useMacAddressLookup = false;
		let useIpAddressLookup = false;
		let serverURL = "";
		let apiClientId = "";
		let apiUsername = "";
		let apiPassword = "";

		// Load in the settings from the input settings variable, and set them to the form controls.
		if (this.inputSettings) {
			enabled = this.inputSettings.Enabled;
			useMacAddressLookup = this.inputSettings.UseMacAddressLookup;
			useIpAddressLookup = this.inputSettings.UseIpAddressLookup;
			serverURL = this.inputSettings.ServerURL;
			apiUsername = this.inputSettings.Username;
			apiPassword = (this.inputSettings.HasPassword ? this.hidePassword(this.inputSettings.Password) : "");
			apiClientId = (this.inputSettings.HasClientID ? this.hidePassword(this.inputSettings.ClientID) : "");
		}

		this.enabled = new UntypedFormControl(enabled);
		this.useMacAddressLookup = new UntypedFormControl(useMacAddressLookup);
		this.useIpAddressLookup = new UntypedFormControl(useIpAddressLookup);
		this.serverURL = new UntypedFormControl(serverURL);
		this.apiUsername = new UntypedFormControl(apiUsername);
		this.apiPassword = new UntypedFormControl(apiPassword);
		this.apiClientId = new UntypedFormControl(apiClientId);

		this.apiUsernameChanged = false;
		this.apiUsernameChangedSub = this.apiUsername.valueChanges.subscribe(() => {
			this.apiUsernameChanged = true;
		});

		this.apiPasswordChanged = false;
		this.apiPasswordChangedSub = this.apiPassword.valueChanges.subscribe(() => {
			this.apiPasswordChanged = true;
		});

		this.apiClientIdChanged = false;
		this.apiClientIdChangedSub = this.apiClientId.valueChanges.subscribe(() => {
			this.apiClientIdChanged = true;
		});


		this.form = this.fb.group({
			enabled: this.enabled,
			useMacAddressLookup: this.useMacAddressLookup,
			useIpAddressLookup: this.useIpAddressLookup,
			serverURL: this.serverURL,
			apiUsername: this.apiUsername,
			apiPassword: this.apiPassword,
			apiClientId: this.apiClientId,
		},
		{
			validator: Validators.compose([
				this.noneOrAllSettings(),
			])
		});
	}

	public enableSave(): boolean {
		return true;
	}

	public onSave(): void {
		const outputIntegrationTable = new IntegrationTable();
		const integrationSettings = this.getIntegrationSettings();
		outputIntegrationTable.Add(integrationSettings);
		this.configService.setIntegrationTable(outputIntegrationTable).subscribe(
			() => {
				this.messageService.displayInfoMessage("Successfully saved Halo default settings");
				this.form.markAsPristine();
			},
			(error) => {
				this.messageService.displayErrorMessage("Failed to save Halo default settings");
			});
	}
	public get enableTestHaloButton(): boolean {
		return (
			!!this.serverURL && !!this.serverURL.value &&
			!!this.apiUsername && !!this.apiUsername.value &&
			!!this.apiPassword && !!this.apiPassword.value &&
			!!this.apiClientId && !!this.apiClientId.value);
	}

	public testConnection(): void {
		let serverURL = this.serverURL.value;
		let apiUsername = "";
		let apiPassword = "";
		let apiClientId = "";

		if (this.apiUsernameChanged) {
			apiUsername = this.apiUsername.value;
		}
		else {
			apiUsername = this.inputSettings.Username;
		}
		if (this.apiPasswordChanged) {
			apiPassword = StringUtils.ObfuscateString2(this.apiPassword.value);
		}
		else {
			apiPassword = this.inputSettings.Password;
		}
		if (this.apiClientIdChanged) {
			apiClientId = StringUtils.ObfuscateString2(this.apiClientId.value);
		}
		else {
			apiClientId = this.inputSettings.ClientID;
		}

		this.configService.testHaloConnection(serverURL, apiClientId, apiUsername, apiPassword).subscribe(
			() => {
				this.messageService.displayInfoMessage("Successfully connected to Halo");
			},
			(error) => {
				this.messageService.displayErrorMessage("Failed to connect to Halo");
			});
	}

	public onUsernameKeyDownHandler(event: any): void {
		if (!this.apiUsernameChanged) {
			this.apiUsernameChanged = true;

			this.apiUsername.setValue('');
			this.apiUsername.markAsDirty();
		}
	}

	public onPasswordKeyDownHandler(event: any): void {
		if (!this.apiPasswordChanged) {
			this.apiPasswordChanged = true;

			this.apiPassword.setValue('');
			this.apiPassword.markAsDirty();
		}
	}

	public onClientIdKeyDownHandler(event: any): void {
		if (!this.apiClientIdChanged) {
			this.apiClientIdChanged = true;

			this.apiClientId.setValue('');
			this.apiClientId.markAsDirty();
		}
	}

	public getIntegrationSettings(): Integration {
		const outputIntegration = new Integration(IntegrationNames.Halo);
		outputIntegration.Enabled = this.enabled.value;
		outputIntegration.UseMacAddressLookup = this.useMacAddressLookup.value;
		outputIntegration.UseIpAddressLookup = this.useIpAddressLookup.value;
		outputIntegration.ServerURL = this.serverURL.value;

		if (this.apiUsernameChanged) {
			outputIntegration.Username = this.apiUsername.value;
		}
		else {
			outputIntegration.Username = this.inputSettings.Username;
		}

		if (this.apiPasswordChanged) {
			outputIntegration.Password = StringUtils.ObfuscateString2(this.apiPassword.value);
		}
		else {
			outputIntegration.Password = this.inputSettings.Password;
		}

		if (this.apiClientIdChanged) {
			outputIntegration.ClientID = StringUtils.ObfuscateString2(this.apiClientId.value);
		}
		else {
			outputIntegration.ClientID = this.inputSettings.ClientID;
		}


		return outputIntegration;
	}

	private noneOrAllSettings(): ValidatorFn {
		return () => {
			let b1 = (!!this.serverURL.value !== !!this.apiClientId.value !== !!this.apiUsername.value !== !!this.apiPassword.value);
			if (b1) {
				return {
					noneOrAllSettings: { valid: false }
				};
			}

			return null;
		}
	}

}
